import { useState } from 'react';
import { Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { useFinancialCustomers } from '../../hooks/useFinancial';
import { formatCurrency, formatDateBR } from '../../utils/format';

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
  ACTIVE: { label: 'Ativo', className: 'bg-green-100 text-green-700' },
  PAST_DUE: { label: 'Inadimplente', className: 'bg-red-100 text-red-700' },
  CANCELED: { label: 'Cancelado', className: 'bg-secondary-100 text-secondary-600' },
  TRIALING: { label: 'Trial', className: 'bg-blue-100 text-blue-700' },
  UNPAID: { label: 'Nao pago', className: 'bg-orange-100 text-orange-700' },
  PAUSED: { label: 'Pausado', className: 'bg-yellow-100 text-yellow-700' },
};

export function CustomersTable() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');

  const { data, isLoading } = useFinancialCustomers({
    page,
    limit: 20,
    search: search || undefined,
    status: statusFilter || undefined,
    plan: planFilter || undefined,
  });

  const customers = data?.data ?? [];
  const pagination = data?.pagination;

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  }

  return (
    <div className="bg-white rounded-xl border border-secondary-200">
      <div className="p-5 border-b border-secondary-200">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-secondary-700">Clientes</h3>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="text-sm border border-secondary-300 rounded-lg px-3 py-1.5"
            >
              <option value="">Todos os status</option>
              <option value="ACTIVE">Ativo</option>
              <option value="PAST_DUE">Inadimplente</option>
              <option value="CANCELED">Cancelado</option>
              <option value="TRIALING">Trial</option>
            </select>
            <select
              value={planFilter}
              onChange={(e) => {
                setPlanFilter(e.target.value);
                setPage(1);
              }}
              className="text-sm border border-secondary-300 rounded-lg px-3 py-1.5"
            >
              <option value="">Todos os planos</option>
              <option value="Pro">Pro</option>
              <option value="Enterprise">Enterprise</option>
              <option value="Free">Free</option>
            </select>
            <form onSubmit={handleSearch} className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Buscar..."
                className="text-sm border border-secondary-300 rounded-lg pl-8 pr-3 py-1.5 w-48"
              />
            </form>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-secondary-200 text-left text-secondary-500">
              <th className="px-5 py-3 font-medium">Nome</th>
              <th className="px-5 py-3 font-medium">Email</th>
              <th className="px-5 py-3 font-medium">Plano</th>
              <th className="px-5 py-3 font-medium">Valor</th>
              <th className="px-5 py-3 font-medium">Status</th>
              <th className="px-5 py-3 font-medium">Ult. Pagamento</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className="px-5 py-8 text-center text-secondary-400">
                  Carregando...
                </td>
              </tr>
            ) : customers.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-5 py-8 text-center text-secondary-400">
                  Nenhum cliente encontrado
                </td>
              </tr>
            ) : (
              customers.map((customer: any) => {
                const statusInfo = STATUS_LABELS[customer.subscriptionStatus] ?? {
                  label: customer.subscriptionStatus ?? '-',
                  className: 'bg-secondary-100 text-secondary-600',
                };
                return (
                  <tr
                    key={customer.id}
                    className="border-b border-secondary-100 hover:bg-secondary-50"
                  >
                    <td className="px-5 py-3 font-medium text-secondary-900">
                      {customer.name || customer.docugenClientName || '-'}
                    </td>
                    <td className="px-5 py-3 text-secondary-600">{customer.email}</td>
                    <td className="px-5 py-3 text-secondary-600">
                      {customer.planName ?? '-'}
                      {customer.seatCount && (
                        <span className="ml-1 text-xs text-secondary-400">
                          ({customer.seatCount} assentos)
                        </span>
                      )}
                      {customer.seatOfEmail && (
                        <span className="block text-xs text-secondary-400">
                          Titular: {customer.seatOfEmail}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-secondary-900 font-medium">
                      {formatCurrency(customer.planAmount, 'BRL', '-')}
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.className}`}
                      >
                        {statusInfo.label}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-secondary-600">
                      {formatDateBR(customer.lastPaymentAt, '-')}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between px-5 py-3 border-t border-secondary-200">
          <span className="text-sm text-secondary-500">
            {pagination.total} cliente{pagination.total !== 1 ? 's' : ''}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded-lg hover:bg-secondary-100 disabled:opacity-40"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-secondary-600">
              {page} / {pagination.totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
              disabled={page === pagination.totalPages}
              className="p-1.5 rounded-lg hover:bg-secondary-100 disabled:opacity-40"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
